export interface VoiceEvidence {
  activeMs: number;
  frames: number;
  peak: number;
}

export interface WordEvidence {
  text?: string;
  start?: number;
  end?: number;
  type?: "word" | "spacing";
  logprob?: number;
}

const shortFillers = new Set(["yeah", "yea", "yes", "yep", "uh", "um", "hmm", "hm", "okay", "ok"]);

export const resetVoiceEvidence = (evidence: VoiceEvidence): void => {
  evidence.activeMs = 0;
  evidence.frames = 0;
  evidence.peak = 0;
};

export const isLikelySilenceArtifact = (
  text: string,
  evidence: VoiceEvidence,
  words?: WordEvidence[],
): boolean => {
  const spokenWords = words?.filter((word) => word.type === "word") ?? [];
  const logprobs = spokenWords
    .map((word) => word.logprob)
    .filter((value): value is number => value !== undefined);
  const averageLogprob = logprobs.length
    ? logprobs.reduce((total, value) => total + value, 0) / logprobs.length
    : undefined;
  const starts = spokenWords.map((word) => word.start).filter((value): value is number => value !== undefined);
  const ends = spokenWords.map((word) => word.end).filter((value): value is number => value !== undefined);
  const providerDurationMs = starts.length && ends.length
    ? (Math.max(...ends) - Math.min(...starts)) * 1_000
    : 0;
  const providerConfident = averageLogprob !== undefined && averageLogprob >= -0.55 && providerDurationMs >= 180;
  const anyLocalSpeech = evidence.frames >= 1 && evidence.peak >= 0.06;
  const localSpeechPresent = evidence.frames >= 3 && evidence.activeMs >= 90 && evidence.peak >= 0.065;
  const normalized = text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();

  if (!anyLocalSpeech) return true;
  if (!localSpeechPresent && !providerConfident) return true;
  if (shortFillers.has(normalized)) {
    const localFillerPresent = evidence.frames >= 4 && evidence.activeMs >= 140 && evidence.peak >= 0.08;
    return !localFillerPresent && !providerConfident;
  }
  return averageLogprob !== undefined && averageLogprob < -1.5 && normalized.length < 12;
};
